import React from 'react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      id: 1,
      title: "AI Virtual Front Desk",
      description: "24/7 AI-powered virtual receptionist that handles patient inquiries, scheduling, and follow-ups.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
        </svg>
      ),
      features: ["24/7 Patient Support", "Automated Scheduling", "Smart Call Routing"],
      path: "/ai-virtual-front-desk"
    },
    {
      id: 2,
      title: "AI-Powered Chatbot",
      description: "Intelligent chatbot that provides instant responses to patient queries and appointment management.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      features: ["Instant Responses", "Appointment Management", "Insurance Verification"],
      path: "/ai-chatbot"
    },
    {
      id: 3,
      title: "Smart Scheduling",
      description: "AI-powered scheduling system that optimizes appointments and reduces no-shows.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      features: ["Predictive Scheduling", "No-Show Prevention", "Smart Waitlist"],
      path: "/smart-scheduling"
    },
    {
      id: 4,
      title: "AI Clinical Assistant",
      description: "Advanced AI tools for clinical documentation, treatment planning, and patient care.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      features: ["Voice-to-Text Notes", "Treatment Suggestions", "Progress Tracking"],
      path: "/ai-clinical-assistant"
    }
  ];

  return (
    <section className="w-full md:w-1/2">
      <div className="space-y-8">
        {/* Section Header */}
        <div className="space-y-4">
          <h2 className="text-4xl font-bold text-gray-900">
            AI-Powered Services
          </h2>
          <p className="text-lg text-gray-600">
            Transform your practice with intelligent automation and AI-driven solutions
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service) => (
            <div 
              key={service.id}
              className="group p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-orange-500"
            >
              {/* Service Header */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-orange-100 text-orange-600 rounded-lg group-hover:bg-orange-600 group-hover:text-white transition-colors">
                  {service.icon}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                  <p className="mt-2 text-gray-600">{service.description}</p>
                </div>
              </div>

              {/* Features List */}
              <div className="mt-4 pl-16">
                <ul className="space-y-2">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <svg className="h-4 w-4 text-orange-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Learn More Link */}
              <div className="mt-4 pl-16">
                <Link 
                  to={service.path}
                  className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
                >
                  Learn more
                  <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="flex justify-center mt-8">
          <button className="bg-orange-500 text-white px-8 py-3 rounded-full hover:bg-orange-600 transition-colors text-lg shadow-lg hover:shadow-xl">
            Explore AI Features
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
